import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Tag,
  Space,
  Modal,
  Form,
  Typography,
  Tooltip,
  Empty,
  App,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  CopyOutlined,
  UserOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import api from '@/api/client';

const { Title, Text } = Typography;
const { Option } = Select;

interface User {
  id: number;
  username: string;
  email: string;
  displayName: string;
  inviteCode?: string;
  inviteCodeType?: string;
  totalInvited: number;
  totalRewards: number;
}

interface InviteCodeInfo {
  hasInviteCode: boolean;
  inviteCode: string | null;
  codeType: string | null;
  codeTypeName: string | null;
  createdAt: string | null;
  totalInvited: number;
  totalRewards: number;
}

export default function InvitesPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [inviteCodeInfo, setInviteCodeInfo] = useState<InviteCodeInfo | null>(null);
  const [form] = Form.useForm();
  const [generating, setGenerating] = useState(false);
  const { message, modal } = App.useApp();

  // 获取用户列表
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users', {
        params: {
          page: 1,
          pageSize: 100,
          search: searchKeyword,
        },
      });
      if (data.success) {
        setUsers(data.data.list);
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || '获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchKeyword]);

  // 生成邀请码
  const handleGenerateInviteCode = async (values: { userId: number; codeType: string }) => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/users/${values.userId}/invite-code`, {
        codeType: values.codeType,
      });
      if (data.success) {
        message.success('邀请码生成成功');
        setIsModalOpen(false);
        form.resetFields();
        fetchUsers();
        // 显示生成的邀请码
        modal.success({
          title: '邀请码生成成功',
          content: (
            <div>
              <p>用户：{data.data.username}</p>
              <p>类型：{data.data.codeTypeName}</p>
              <p>
                邀请码：
                <Text copyable={{ text: data.data.inviteCode }} strong style={{ fontSize: 16 }}>
                  {data.data.inviteCode}
                </Text>
              </p>
            </div>
          ),
        });
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || '生成邀请码失败');
    } finally {
      setGenerating(false);
    }
  };

  // 查看邀请码详情
  const handleViewInviteCode = async (user: User) => {
    setSelectedUser(user);
    try {
      const { data } = await api.get(`/users/${user.id}/invite-code`);
      if (data.success) {
        setInviteCodeInfo(data.data);
        setIsDetailModalOpen(true);
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || '获取邀请码信息失败');
    }
  };

  // 复制邀请码
  const handleCopyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code);
    message.success('邀请码已复制');
  };

  const columns = [
    {
      title: '用户ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      render: (text: string, record: User) => (
        <Space>
          <UserOutlined />
          <span>{text || record.displayName || record.email}</span>
        </Space>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
    },
    {
      title: '邀请码状态',
      key: 'inviteStatus',
      render: (_: any, record: User) => {
        if (record.inviteCode) {
          return (
            <Tag color={record.inviteCodeType === 'beta' ? 'purple' : 'blue'}>
              {record.inviteCodeType === 'beta' ? '内测邀请码' : '普通邀请码'}
            </Tag>
          );
        }
        return <Tag>未生成</Tag>;
      },
    },
    {
      title: '已邀请人数',
      dataIndex: 'totalInvited',
      width: 120,
      render: (count: number) => (
        <Text type={count > 0 ? 'success' : 'secondary'}>{count || 0} 人</Text>
      ),
    },
    {
      title: '累计奖励',
      dataIndex: 'totalRewards',
      width: 120,
      render: (rewards: number) => (
        <Text type={rewards > 0 ? 'success' : 'secondary'}>{rewards || 0} 积分</Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: User) => (
        <Space>
          {record.inviteCode ? (
            <>
              <Tooltip title="复制邀请码">
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyInviteCode(record.inviteCode!)}
                />
              </Tooltip>
              <Button type="link" onClick={() => handleViewInviteCode(record)}>
                查看详情
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedUser(record);
                form.setFieldsValue({ userId: record.id });
                setIsModalOpen(true);
              }}
            >
              生成邀请码
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <GiftOutlined />
            <Title level={4} style={{ margin: 0 }}>邀请码管理</Title>
          </Space>
        }
        extra={
          <Space>
            <Input
              placeholder="搜索用户名/邮箱"
              prefix={<SearchOutlined />}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onPressEnter={fetchUsers}
              style={{ width: 200 }}
            />
            <Button type="primary" onClick={fetchUsers}>
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 生成邀请码弹窗 */}
      <Modal
        title="生成邀请码"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleGenerateInviteCode}
        >
          <Form.Item
            name="userId"
            label="选择用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              placeholder="请选择用户"
              showSearch
              optionFilterProp="children"
              disabled={!!selectedUser}
            >
              {users.map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.username || user.displayName || user.email} (ID: {user.id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="codeType"
            label="邀请码类型"
            initialValue="normal"
            rules={[{ required: true, message: '请选择邀请码类型' }]}
          >
            <Select placeholder="请选择邀请码类型">
              <Option value="normal">
                <Space>
                  <Tag color="blue">普通邀请码</Tag>
                  <Text type="secondary">用户注册时绑定邀请关系</Text>
                </Space>
              </Option>
              <Option value="beta">
                <Space>
                  <Tag color="purple">内测邀请码</Tag>
                  <Text type="secondary">用户获得天使产品身份</Text>
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsModalOpen(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={generating}>
                生成邀请码
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 邀请码详情弹窗 */}
      <Modal
        title="邀请码详情"
        open={isDetailModalOpen}
        onCancel={() => setIsDetailModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsDetailModalOpen(false)}>
            关闭
          </Button>,
        ]}
      >
        {inviteCodeInfo && inviteCodeInfo.hasInviteCode ? (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text type="secondary">邀请码：</Text>
              <br />
              <Text copyable strong style={{ fontSize: 18 }}>
                {inviteCodeInfo.inviteCode}
              </Text>
            </div>
            <div>
              <Text type="secondary">类型：</Text>
              <br />
              <Tag color={inviteCodeInfo.codeType === 'beta' ? 'purple' : 'blue'}>
                {inviteCodeInfo.codeTypeName}
              </Tag>
            </div>
            <div>
              <Text type="secondary">生成时间：</Text>
              <br />
              <Text>{inviteCodeInfo.createdAt ? new Date(inviteCodeInfo.createdAt).toLocaleString() : '-'}</Text>
            </div>
            <div>
              <Text type="secondary">已邀请人数：</Text>
              <br />
              <Text strong>{inviteCodeInfo.totalInvited} 人</Text>
            </div>
            <div>
              <Text type="secondary">累计获得奖励：</Text>
              <br />
              <Text strong type="success">{inviteCodeInfo.totalRewards} 积分</Text>
            </div>
          </Space>
        ) : (
          <Empty description="该用户暂无邀请码" />
        )}
      </Modal>
    </div>
  );
}
